import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { isPlatformBrowser } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    Output,
    PLATFORM_ID,
} from "@angular/core";
import { LocalComponentStore } from "@dtm-frontend/shared/utils";
import { UntilDestroy } from "@ngneat/until-destroy";

interface FormFieldComponentState {
    hasErrors: boolean;
    isDisabled: boolean;
    isFocused: boolean;
    isActivated: boolean;
    errorId: string | null;
}

@UntilDestroy()
@Component({
    selector: "dtm-ui-form-field",
    templateUrl: "./form-field.component.html",
    styleUrls: ["./form-field.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class FormFieldComponent implements AfterViewInit {
    @Input() public set isDisabled(value: BooleanInput) {
        this.localStore.patchState({ isDisabled: coerceBooleanProperty(value) });
    }

    @Input() public set isFocused(value: BooleanInput) {
        this.localStore.patchState({ isFocused: coerceBooleanProperty(value) });
    }

    @Input() public set isActivated(value: BooleanInput) {
        this.localStore.patchState({ isActivated: coerceBooleanProperty(value) });
    }

    @Input() public set hasErrors(value: BooleanInput) {
        this.localStore.patchState({ hasErrors: coerceBooleanProperty(value) });
    }

    @Output() public contentClick = new EventEmitter<void>();

    protected hasErrors$ = this.localStore.selectByKey("hasErrors");
    protected isDisabled$ = this.localStore.selectByKey("isDisabled");
    protected isFocused$ = this.localStore.selectByKey("isFocused");
    protected isActivated$ = this.localStore.selectByKey("isActivated");

    constructor(
        private readonly localStore: LocalComponentStore<FormFieldComponentState>,
        private readonly element: ElementRef<HTMLHtmlElement>,
        @Inject(PLATFORM_ID) private platformId: string
    ) {
        this.localStore.setState({
            hasErrors: false,
            isDisabled: false,
            isFocused: false,
            isActivated: false,
            errorId: null,
        });
    }

    public ngAfterViewInit(): void {
        this.updateAriaDescribedBy();
    }

    public getWrapperElement(): HTMLElement | null {
        return this.element.nativeElement.querySelector(".input-wrapper");
    }

    public getLabelElement(): HTMLElement | null {
        return this.element.nativeElement.querySelector("label");
    }

    protected setHasErrors(hasErrors: boolean) {
        this.localStore.patchState({ hasErrors });
    }

    protected handleErrorIdChange(errorId: string | null) {
        this.localStore.patchState({ errorId });
        this.updateAriaDescribedBy();
    }

    private updateAriaDescribedBy(): void {
        const errorId = this.localStore.selectSnapshotByKey("errorId");
        if (!isPlatformBrowser(this.platformId) || !errorId) {
            return;
        }
        const inputElement = this.element.nativeElement.querySelector("input, textarea");
        if (!inputElement) {
            return;
        }
        inputElement.setAttribute("aria-describedby", errorId);
    }
}
